import { formatFileSize, pluralize, tv } from '@joyful/utils'
import type { VariantProps } from '@joyful/utils'
import { CloudUploadIcon, Trash2Icon } from 'lucide-react'
import type { ReactNode } from 'react'
import type { DropzoneState, FileRejection } from 'react-dropzone'

import { Box } from '../Box'
import { IconButton } from '../Button'
import { Card } from '../Card'
import { Ellipsis } from '../Ellipsis'
import { FileTypeIcon } from '../FileTypeIcon'
import { Label } from '../Label'
import { Stack, StackItem } from '../Stack'
import type { FileWithPreview, UseFileUploadReturn } from '../useFileUpload'

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024

const fileUploadDropzoneVariants = tv({
  base: 'w-full cursor-pointer rounded-lg border-2 border-dashed text-center transition-colors disabled:opacity-50',

  defaultVariants: {
    dragState: 'idle',
  },

  variants: {
    dragState: {
      active: 'enabled:border-tremor-brand enabled:bg-tremor-brand-faint',
      idle: 'enabled:border-tremor-border enabled:hover:border-tremor-content-subtle',
      reject: 'enabled:border-category-red enabled:bg-tremor-background-muted',
    },
  },
})

export interface FileUploadFieldProps extends Pick<
  UseFileUploadReturn,
  'dropzone' | 'removeFile' | 'rejectedFiles'
> {
  density?: 'compact'
  disabled?: boolean
  error?: string
  files: Array<FileWithPreview>
  isUploading?: boolean
  label?: ReactNode
  maxFiles?: number
  maxSize?: number
  multiple?: boolean
  name: string
  onChange: (files: Array<FileWithPreview>) => void
  onUpload?: (files: Array<FileWithPreview>) => void
}

export function FileUploadField({
  density,
  disabled = false,
  dropzone,
  error,
  files,
  isUploading = false,
  label,
  maxFiles = 5,
  maxSize = DEFAULT_MAX_FILE_SIZE,
  multiple = true,
  name,
  rejectedFiles,
  removeFile,
}: FileUploadFieldProps) {
  return (
    <Stack gap="sm" width="full">
      {label === null || label === undefined ? null : (
        <Label htmlFor={name}>{label}</Label>
      )}
      <FileUploadDropzone
        density={density}
        disabled={disabled}
        dropzone={dropzone}
        isUploading={isUploading}
        maxFiles={maxFiles}
        maxSize={maxSize}
        multiple={multiple}
        name={name}
        selectedFileCount={files.length}
      />
      <FileUploadSelectedFiles
        files={files}
        isUploading={isUploading}
        removeFile={removeFile}
      />
      <FileUploadRejectedMessages rejectedFiles={rejectedFiles} />
      {error ? <p className="text-category-red text-sm">{error}</p> : null}
    </Stack>
  )
}

export { fileUploadDropzoneVariants }

function FileUploadDropzone({
  density,
  disabled,
  dropzone,
  isUploading,
  maxFiles,
  maxSize,
  multiple,
  name,
  selectedFileCount,
}: {
  density: FileUploadFieldProps['density']
  disabled: boolean
  dropzone: DropzoneState
  isUploading: boolean
  maxFiles: number
  maxSize: number
  multiple: boolean
  name: string
  selectedFileCount: number
}) {
  const canAddMore = multiple
    ? selectedFileCount < maxFiles
    : selectedFileCount === 0

  if (!canAddMore) {
    return null
  }

  const isCompactDensity = density === 'compact'

  return (
    <button
      type="button"
      {...dropzone.getRootProps()}
      className={fileUploadDropzoneVariants({
        dragState: getDropzoneDragState(dropzone),
      })}
      disabled={isUploading || disabled}
    >
      <input {...dropzone.getInputProps()} name={name} />
      <Box padding={isCompactDensity ? 'md' : 'lg'}>
        <Stack align="center" gap="sm">
          <CloudUploadIcon
            className="text-tremor-content"
            size={isCompactDensity ? 20 : 32}
          />
          <Stack align="center" gap="xs">
            <p className="text-tremor-content text-sm font-medium">
              {getDropzoneTitle({
                isDragActive: dropzone.isDragActive,
                isDragReject: dropzone.isDragReject,
              })}
            </p>
            <p className="text-tremor-content-subtle text-xs">
              {getDropzoneLimitsText({ maxFiles, maxSize, multiple })}
            </p>
            {isCompactDensity ? null : (
              <p className="text-tremor-content-subtle text-xs">
                {`Supports: PDF, JPG, PNG, DOC, DOCX, TXT, CSV`}
              </p>
            )}
          </Stack>
        </Stack>
      </Box>
    </button>
  )
}

function FileUploadSelectedFiles({
  files,
  isUploading,
  removeFile,
}: {
  files: Array<FileWithPreview>
  isUploading: boolean
  removeFile: FileUploadFieldProps['removeFile']
}) {
  if (files.length === 0) {
    return null
  }

  return (
    <Stack asChild gap="sm">
      <ul>
        {files.map((file, index) => {
          return (
            <FileUploadSelectedFile
              file={file}
              index={index}
              isUploading={isUploading}
              key={`${file.name}-${file.size}-${file.lastModified}`}
              removeFile={removeFile}
            />
          )
        })}
      </ul>
    </Stack>
  )
}

function FileUploadSelectedFile({
  file,
  index,
  isUploading,
  removeFile,
}: {
  file: FileWithPreview
  index: number
  isUploading: boolean
  removeFile: FileUploadFieldProps['removeFile']
}) {
  return (
    <Card asChild size="xs">
      <li>
        <Stack align="center" direction="horizontal" gap="sm" justify="between">
          <Stack align="center" direction="horizontal" gap="sm" grow="fill">
            <FileTypeIcon fileName={file.name} size={20} />
            <StackItem share="fill">
              <Ellipsis tone="strong" size="sm" weight="medium">
                {file.name}
              </Ellipsis>
              <p className="text-tremor-content text-xs">
                {formatFileSize(file.size)}
              </p>
            </StackItem>
          </Stack>
          <IconButton
            disabled={isUploading}
            onClick={() => {
              removeFile(index)
            }}
            tooltip={`Remove file`}
            variant="ghost"
          >
            <Trash2Icon />
          </IconButton>
        </Stack>
      </li>
    </Card>
  )
}

function FileUploadRejectedMessages({
  rejectedFiles,
}: {
  rejectedFiles: Array<FileRejection>
}) {
  if (rejectedFiles.length === 0) {
    return null
  }

  return (
    <Stack gap="xs">
      {rejectedFiles.map(({ errors, file }) => {
        return (
          <p
            key={`${file.name}-${file.size}-${file.lastModified}`}
            className="text-category-red text-sm"
          >
            {`${file.name}: ${errors
              .map((fileError) => {
                return fileError.message
              })
              .join(', ')}`}
          </p>
        )
      })}
    </Stack>
  )
}

function getDropzoneDragState(
  dropzone: Pick<DropzoneState, 'isDragActive' | 'isDragReject'>,
): NonNullable<VariantProps<typeof fileUploadDropzoneVariants>['dragState']> {
  if (dropzone.isDragReject) {
    return 'reject'
  }

  if (dropzone.isDragActive) {
    return 'active'
  }

  return 'idle'
}

function getDropzoneTitle({
  isDragActive,
  isDragReject,
}: {
  isDragActive: boolean
  isDragReject: boolean
}) {
  if (isDragReject) {
    return `Some files will be rejected`
  }

  if (isDragActive) {
    return `Drop the files here…`
  }

  return `Drag & drop files here or click to browse`
}

function getDropzoneLimitsText({
  maxFiles,
  maxSize,
  multiple,
}: {
  maxFiles: number
  maxSize: number
  multiple: boolean
}) {
  if (multiple) {
    return `Up to ${pluralize(maxFiles, 'file')}, ${formatFileSize(maxSize)} max each`
  }

  return `Single file, ${formatFileSize(maxSize)} max`
}
